import * as React from 'react';
import { Component } from 'react';
import { SchoolEvent } from '../../../../../../../models/event';
import propz from 'propz';
import classNames from 'classnames';
import {
  convertPointsToStringWithoutDelimeter,
  convertStringToPoints,
  getDefaultDigitMask,
  getDefaultTimeOrDistanceMask,
  getHouseScoreByHouseId,
  getHouseScoreId,
  getIndividualScoreByStudent,
  getIsErrorHashMapFromDataHashMap,
  getMask,
  getPlayerScoreId,
  getScorePointsForTeamEvents,
  getTeamScoreByTeamId,
  getTeamScoreId,
  isSportPointsDisplayPlain
} from '../../../../../../../helpers/event/schoolEventViewHelper';
import * as TextMaskCore from 'text-mask-core';
import { EMPTY_STRING } from '../../../../../../../consts/date';
import { Button } from '../../../../../../../components/Button/Button';
import './Results.scss';
import { Checkbox } from '../../../../../../../components/Checkbox/Checkbox';
import { getRegexpFromSportSign } from '../../../../../../../helpers/sport/sport';
import { isMultipartyEvent } from '../../../../../../../helpers/event/event';
import { EVENT_RESULTS_MODE } from '../../../../../../../consts/event';
import { ResultsMode } from './ResultsMode';
import { Switch } from '../../../../../../../components/Switch/Switch';
import { sortHouses } from '../../../../../../../helpers/house/house';
import { ARROW_DOWN_KEY, ARROW_UP_KEY, ENTER_KEY } from '../../../../../../../consts/common';

interface Props {
  event: SchoolEvent;
  onSaveClick: (data, resultsMode?: string) => void;
  onCloseClick: () => void;
}
interface State {
  mask: boolean | (string | RegExp)[];
  dataHashMap: DataHashMap;
  players: Player[];
  teams: Team[];
  houses: House[];
  continuousCount: number;
  resultsMode: string;
}

export interface DataHashMap {
  [key: string]: {
    value: string;
    isError: boolean;
    oldValue: string;
    continuousCount: number;
    isInputEnabled: boolean;
    //only for team
    playerIds?: string[];
    teamPoints?: number;
    //only for player
    teamId?: string;
  };
}

interface Player {
  id: string;
  userId: string;
  permissionId: string;
  teamId: string;
  continuousCount: number;
}

interface Team {
  id: string;
  continuousCount: number;
  houseId: string;
}

interface House {
  id: string;
  name: string;
  continuousCount: number;
}

export class TeamInternalHousesResults extends Component<Props, State> {
  inputElement: any[];
  textMaskInputElement: any[];

  constructor(props: Props) {
    super(props);

    this.inputElement = [];
    this.textMaskInputElement = [];

    const { event } = props;
    const mask = propz.get(event, ['sport', 'points', 'inputMask']);

    const inputMask = getMask(mask);

    const { teamsData, housesData } = event;
    let players = [];
    let teams = [];

    let continuousCount = 0;
    let dataHashMap: DataHashMap = {};

    const houses = [...housesData]
      .sort((house1, house2) => sortHouses(event, house1, house2))
      .map(house => {
        const team = teamsData.find(teamData => teamData.houseId === house.id);
        const isTeamExist = typeof team !== 'undefined';
        const houseId = house.id;
        if (!isTeamExist) {
          continuousCount++;

          const housePoints = getHouseScoreByHouseId(event, houseId);
          const housePointsStr = convertPointsToStringWithoutDelimeter(event, housePoints);
          const hasHouseScore = event.results.houseScore.some(houseScore => houseScore.houseId === houseId);

          dataHashMap[house.id] = {
            value: hasHouseScore ? housePointsStr : '',
            oldValue: hasHouseScore ? housePointsStr : '',
            isError: false,
            continuousCount: continuousCount,
            isInputEnabled: true
          };
        } else {
          const teamId = team.id;
          const teamPoints = getTeamScoreByTeamId(event, teamId);
          const teamPointsStr = convertPointsToStringWithoutDelimeter(event, teamPoints);
          const hasTeamScore = event.results.teamScore.some(teamScore => teamScore.teamId === teamId);

          continuousCount++;

          dataHashMap[teamId] = {
            value: hasTeamScore ? teamPointsStr : '',
            oldValue: hasTeamScore ? teamPointsStr : '',
            isError: false,
            continuousCount: continuousCount,
            playerIds: team.players.map(player => player.id),
            isInputEnabled: true,
            teamPoints: teamPoints
          };

          teams.push({ id: teamId, continuousCount, houseId });

          team.players.forEach(({ id, userId, permissionId }) => {
            const playerPoints = getIndividualScoreByStudent(event, userId, permissionId);
            const playerPointsStr = convertPointsToStringWithoutDelimeter(event, playerPoints);

            continuousCount++;

            dataHashMap[id] = {
              value: playerPoints === 0 ? '' : playerPointsStr,
              oldValue: playerPoints === 0 ? '' : playerPointsStr,
              isError: false,
              continuousCount: continuousCount,
              isInputEnabled: false,
              teamId: teamId
            };

            players.push({ id, userId, permissionId, continuousCount, teamId });
          });
        }

        return {
          id: house.id,
          name: house.name,
          continuousCount
        };
      });

    const defaultResultsMode = isMultipartyEvent(event) ? EVENT_RESULTS_MODE.PLACES : EVENT_RESULTS_MODE.RESULTS;
    const resultsMode = propz.get(event, ['resultsMode'], defaultResultsMode);

    this.state = {
      mask: inputMask,
      dataHashMap: dataHashMap,
      players: players,
      teams: teams,
      continuousCount: continuousCount,
      houses: houses,
      resultsMode: resultsMode
    };
  }

  componentDidMount() {
    this.initTextMask();
  }

  initTextMask() {
    const { dataHashMap, continuousCount, players, teams, mask, houses } = this.state;
    const { event } = this.props;

    for (let i = 1; i <= continuousCount; i++) {
      const playersIndex = players.findIndex(player => player.continuousCount === i);
      const teamsIndex = teams.findIndex(team => team.continuousCount === i);
      const housesIndex = houses.findIndex(house => house.continuousCount === i);

      const isPlayer = playersIndex !== -1;
      const isTeam = teamsIndex !== -1;
      const isHouse = housesIndex !== -1;

      let id;

      switch (true) {
        case isPlayer:
          id = players[playersIndex].id;
          break;
        case isTeam:
          id = teams[teamsIndex].id;
          break;
        case isHouse:
          id = houses[housesIndex].id;
          break;
      }

      const { value, isInputEnabled } = dataHashMap[id];

      switch (true) {
        //very specific condition
        case mask && isTeam && !isInputEnabled && !isSportPointsDisplayPlain(event) && !isMultipartyEvent(event):
          this.textMaskInputElement[i] = TextMaskCore.createTextMaskInputElement({
            inputElement: this.inputElement[i],
            mask: inputValue => getDefaultTimeOrDistanceMask(event, inputValue),
            showMask: false
          });
          break;
        default:
          this.textMaskInputElement[i] = TextMaskCore.createTextMaskInputElement({
            inputElement: this.inputElement[i],
            mask: mask || (inputValue => getDefaultDigitMask(event, inputValue)),
            showMask: !mask
          });
      }

      this.textMaskInputElement[i].update(value);
    }
  }

  onSubmit = () => {
    const { dataHashMap, players, teams, houses, resultsMode } = this.state;
    const { event, onSaveClick } = this.props;

    const isErrorHashMap = getIsErrorHashMapFromDataHashMap(event, dataHashMap, players, houses, teams);

    const isErrorPlayers = players.some(player => isErrorHashMap[player.id] === true);
    const isErrorTeams = teams.some(team => isErrorHashMap[team.id] === true);
    const isErrorHouses = houses.some(house => isErrorHashMap[house.id] === true);

    if (isErrorPlayers || isErrorTeams || isErrorHouses) {
      let dataHashMapUpdated: DataHashMap = {};

      for (let dataId in dataHashMap) {
        dataHashMapUpdated[dataId] = {
          ...dataHashMap[dataId],
          isError: isErrorHashMap[dataId]
        };
      }

      this.setState({
        dataHashMap: dataHashMapUpdated
      });
      return;
    }

    const playerScores = players.map(({ userId, permissionId, id, teamId }) => {
      return {
        userId: userId,
        permissionId: permissionId,
        teamId: teamId,
        score: getScorePointsForTeamEvents(event, dataHashMap, id),
        scoreId: getPlayerScoreId(event, userId, permissionId)
      };
    });

    const teamScores = teams.map(({ id }) => {
      const { isInputEnabled, teamPoints } = dataHashMap[id];
      return {
        teamId: id,
        score: isInputEnabled ? getScorePointsForTeamEvents(event, dataHashMap, id) : teamPoints,
        scoreId: getTeamScoreId(event, id)
      };
    });

    const houseScores = houses
      .filter(house => !teams.some(team => team.houseId === house.id))
      .map(({ id }) => {
        return {
          houseId: id,
          score: getScorePointsForTeamEvents(event, dataHashMap, id),
          scoreId: getHouseScoreId(event, id)
        };
      });

    const allScores = [...playerScores, ...teamScores, ...houseScores];

    onSaveClick(allScores, resultsMode);
  };

  onChange(eventDescriptor: React.ChangeEvent<HTMLInputElement>, index: number, id: string) {
    const { dataHashMap } = this.state;
    const { event } = this.props;

    const value = eventDescriptor.target.value;

    let dataHashMapUpdated: DataHashMap = { ...dataHashMap };

    for (let dataId in dataHashMap) {
      if (dataId === id) {
        dataHashMapUpdated[dataId] = {
          ...dataHashMap[dataId],
          isError: false,
          value: value
        };
      }
    }

    this.setState({
      dataHashMap: dataHashMapUpdated
    });

    this.textMaskInputElement[index].update(value);
  }

  onKeyDown = (eventDescriptor, index: number) => {
    const { continuousCount } = this.state;

    const isKeyEnter = eventDescriptor.key === ENTER_KEY;
    const isKeyArrowUp = eventDescriptor.key === ARROW_UP_KEY;
    const isKeyArrowDown = eventDescriptor.key === ARROW_DOWN_KEY;
    const isLastInput = index === continuousCount;
    const isFirstInput = index === 1;

    switch (true) {
      case isKeyEnter && !isLastInput:
      case isKeyArrowDown && !isLastInput:
        this.inputElement[index + 1].focus();
        break;
      case isKeyArrowUp && !isFirstInput:
        this.inputElement[index - 1].focus();
        break;
    }
  };

  onFocus(e, index: number, id: string) {
    const { dataHashMap } = this.state;
    const value = dataHashMap[id].value;

    let dataHashMapUpdated: DataHashMap = {};

    for (let dataId in dataHashMap) {
      if (dataId === id) {
        dataHashMapUpdated[dataId] = {
          ...dataHashMap[dataId],
          oldValue: value
        };
      } else {
        dataHashMapUpdated[dataId] = {
          ...dataHashMap[dataId]
        };
      }
    }

    this.setState({
      dataHashMap: dataHashMapUpdated
    });

    this.textMaskInputElement[index].update('');

    e.stopPropagation();
  }

  onBlur(e, index: number, id: string) {
    const eventValue = e.target.value;
    const { dataHashMap } = this.state;
    const { event } = this.props;

    let dataHashMapUpdated: DataHashMap = { ...dataHashMap };

    if (eventValue === EMPTY_STRING) {
      for (let dataId in dataHashMap) {
        if (dataId === id) {
          dataHashMapUpdated[id] = {
            ...dataHashMap[id],
            value: dataHashMap[id].oldValue
          };
        } else {
          dataHashMapUpdated[dataId] = {
            ...dataHashMap[dataId]
          };
        }
      }
      this.setState({
        dataHashMap: dataHashMapUpdated
      });
      this.textMaskInputElement[index].update(dataHashMap[id].oldValue);
    } else {
      for (let schoolId in dataHashMap) {
        if (schoolId === id) {
          dataHashMapUpdated[id] = {
            ...dataHashMap[id],
            value: eventValue
          };

          const isTeamIdExist = typeof dataHashMap[schoolId].teamId !== 'undefined';
          if (isTeamIdExist) {
            const teamId = dataHashMap[schoolId].teamId;
            const { playerIds } = dataHashMap[teamId];

            let playersValue = 0;

            playerIds.forEach(playerId => {
              const playerValue = dataHashMapUpdated[playerId].value;
              const playerValueCleaned = playerValue.replace('_', '');
              const points = convertStringToPoints(event, playerValueCleaned);
              playersValue += isNaN(points) ? 0 : points;
            });

            const pointsStr = convertPointsToStringWithoutDelimeter(event, playersValue);

            const teamContinuousCount = dataHashMapUpdated[teamId].continuousCount;
            dataHashMapUpdated[teamId] = {
              ...dataHashMap[teamId],
              isError: false,
              value: pointsStr,
              teamPoints: playersValue
            };

            //only for sport points plain
            const regExpSportSign = getRegexpFromSportSign(event);

            if (isSportPointsDisplayPlain(event) && regExpSportSign.test(eventValue)) {
              this.textMaskInputElement[teamContinuousCount].update(pointsStr);
            } else {
              this.textMaskInputElement[teamContinuousCount].update(pointsStr);
            }
          }
        } else {
          dataHashMapUpdated[schoolId] = {
            ...dataHashMap[schoolId]
          };
        }
      }

      this.setState({
        dataHashMap: dataHashMapUpdated
      });

      this.textMaskInputElement[index].update(eventValue);
    }

    e.stopPropagation();
  }

  renderInput(index: number, id: string): React.ReactNode {
    const { dataHashMap } = this.state;
    return (
      <input
        ref={el => (this.inputElement[index] = el)}
        onKeyDown={event => this.onKeyDown(event, index)}
        onChange={event => this.onChange(event, index, id)}
        onFocus={event => this.onFocus(event, index, id)}
        onBlur={event => this.onBlur(event, index, id)}
        className={
          dataHashMap[id].isError === true ? 'eTeamInterHousesResultsInput mError' : 'eTeamInterHousesResultsInput'
        }
        disabled={!dataHashMap[id].isInputEnabled}
      />
    );
  }

  onIndividualResultsChange = (teamId: string): void => {
    const { dataHashMap, mask } = this.state;
    const { event } = this.props;

    let dataHashMapUpdated: DataHashMap = { ...dataHashMap };

    const team = dataHashMap[teamId];

    if (team.isInputEnabled) {
      dataHashMapUpdated[teamId] = {
        ...team,
        oldValue: team.value,
        value: '',
        isInputEnabled: !team.isInputEnabled
      };
      dataHashMapUpdated[teamId].playerIds.forEach(playerId => {
        const player = dataHashMap[playerId];
        dataHashMapUpdated[playerId] = {
          ...player,
          value: '',
          isInputEnabled: !player.isInputEnabled
        };
        this.textMaskInputElement[player.continuousCount].update('');
      });
      if (isSportPointsDisplayPlain(event)) {
        this.textMaskInputElement[team.continuousCount].update('');
      } else {
        const { continuousCount } = dataHashMapUpdated[teamId];

        this.textMaskInputElement[continuousCount] = TextMaskCore.createTextMaskInputElement({
          inputElement: this.inputElement[continuousCount],
          mask: inputValue => getDefaultTimeOrDistanceMask(event, inputValue),
          showMask: false
        });

        this.textMaskInputElement[team.continuousCount].update('');
      }
    } else {
      dataHashMapUpdated[teamId] = {
        ...team,
        value: team.oldValue,
        isInputEnabled: !team.isInputEnabled
      };
      dataHashMapUpdated[teamId].playerIds.forEach(playerId => {
        const player = dataHashMap[playerId];
        dataHashMapUpdated[playerId] = {
          ...player,
          oldValue: player.value,
          value: '',
          isInputEnabled: !player.isInputEnabled
        };
        this.textMaskInputElement[player.continuousCount].update('');
      });
      if (isSportPointsDisplayPlain(event)) {
        this.textMaskInputElement[team.continuousCount].update(team.oldValue);
      } else {
        const { continuousCount } = dataHashMapUpdated[teamId];

        this.textMaskInputElement[continuousCount] = TextMaskCore.createTextMaskInputElement({
          inputElement: this.inputElement[continuousCount],
          mask: mask,
          showMask: false
        });

        this.textMaskInputElement[team.continuousCount].update(team.oldValue);
      }
    }

    this.setState({
      dataHashMap: dataHashMapUpdated
    });
  };

  onResultsModeClick = (resultsMode: string): void => {
    const { dataHashMap } = this.state;

    let dataHashMapUpdated = { ...dataHashMap };

    for (let dataId in dataHashMap) {
      const isTeam = typeof dataHashMap[dataId].teamId !== 'undefined';

      if (isTeam) {
        dataHashMapUpdated[dataId] = {
          ...dataHashMap[dataId],
          isError: false,
          value: '',
          oldValue: '',
          teamPoints: 0
        };
      } else {
        dataHashMapUpdated[dataId] = {
          ...dataHashMap[dataId],
          isError: false,
          value: '',
          oldValue: ''
        };
      }

      this.textMaskInputElement[dataHashMap[dataId].continuousCount].update('');
    }

    this.setState({
      dataHashMap: dataHashMapUpdated,
      resultsMode
    });
  };

  render() {
    const { event, onCloseClick } = this.props;
    const { dataHashMap, houses, resultsMode } = this.state;
    const { teamsData } = event;

    let count = 0;

    return (
      <div>
        <ResultsMode
          isMultipartyEvent={isMultipartyEvent(event)}
          resultsMode={resultsMode}
          onResultsModeClick={this.onResultsModeClick}
        />
        {houses.map(({ id: houseId, name }, index) => {
          const team = teamsData.find(teamData => teamData.houseId === houseId);
          const isTeamExist = typeof team !== 'undefined';

          if (!isTeamExist) {
            count++;

            return (
              <table className={'eTeamInterHousesResultsTable'} key={`houses_inter_houses_results_table_${index}`}>
                <tbody>
                  <tr key={`teams_inter_houses_results_${index}`}>
                    <td colSpan={3} className={'eTeamInterHousesResultsHouseName'}>
                      {name}
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2} className={'eTeamInterHousesResultsCell'} style={{ width: '50%' }}>
                      Total:
                    </td>
                    <td className={'eTeamInterHousesResultsCell'} style={{ width: '50%' }}>
                      {this.renderInput(count, houseId)}
                    </td>
                  </tr>
                </tbody>
              </table>
            );
          } else {
            const { id: teamId, name, players, house } = team;
            count++;
            const isPlayersExist = dataHashMap[teamId].playerIds.length !== 0;

            const hidePlayersListClasses = classNames({
              mHidden: dataHashMap[teamId].isInputEnabled
            });

            return (
              <table className={'eTeamInterHousesResultsTable'} key={`teams_inter_houses_results_table_${index}`}>
                <tbody>
                  <tr key={`teams_inter_houses_results_${index}`}>
                    <td colSpan={3} className={'eTeamInterHousesResultsTeamName'}>
                      {`${name} / ${house.name}`}
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2} className={'eTeamInterHousesResultsCell'} style={{ width: '50%' }}>
                      Total:
                    </td>
                    <td className={'eTeamInterHousesResultsCell'} style={{ width: '50%' }}>
                      {this.renderInput(count, teamId)}
                    </td>
                  </tr>
                  {isPlayersExist && !isMultipartyEvent(event) && (
                    <tr>
                      <td colSpan={3} className={'eTeamExternalSchoolsResultsCell'}>
                        <div className="eIndividualScoringCheck">
                          <Switch
                            name={`indScoring_${teamId}`}
                            label="Individual scoring"
                            value={!dataHashMap[teamId].isInputEnabled}
                            onChange={() => this.onIndividualResultsChange(teamId)}
                            disabled={isMultipartyEvent(event)}
                          />
                        </div>
                      </td>
                    </tr>
                  )}

                  {players.map(({ firstName, lastName, id }, playerIndex) => {
                    count++;
                    return (
                      <tr key={`teams_inter_teams_results_player_${playerIndex}`} className={hidePlayersListClasses}>
                        <td className={'eTeamInternalResultsCell'}>{playerIndex + 1}.</td>
                        <td className={'eTeamInternalResultsCell'}>{`${firstName} ${lastName}`}</td>
                        <td className={'eTeamInternalResultsCell'}>{this.renderInput(count, id)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            );
          }
        })}
        <div className="eResultsActions">
          <Button text={'Cancel'} onClick={onCloseClick} customClass={'btn-secondary'} />
          <Button text={'Save results'} onClick={this.onSubmit} />
        </div>
      </div>
    );
  }
}
